import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { TrendUp, CurrencyDollar, HandCoins, Envelope, ArrowRight, Lightning, Circle, Warning, DownloadSimple } from "@phosphor-icons/react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { metricsApi, debtorApi, authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import RiskBadge from "@/components/RiskBadge";
import { fmtCurrency as fmt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-md p-5 transition-all duration-200 hover:shadow-sm">
      <div className="flex items-start justify-between">
        <span className="overline">{label}</span>
        <Icon size={18} weight="duotone" className="text-primary" />
      </div>
      <div className="mt-3 font-heading text-3xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

const riskColors = { Bajo: "hsl(123 21% 36%)", Medio: "hsl(35 90% 45%)", Alto: "hsl(12 59% 49%)" };

export default function DashboardPage() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [top, setTop] = useState([]);
  const [activosCount, setActivosCount] = useState(0);
  const [promises, setPromises] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    try {
      const [metrics, debtors, prom, usg] = await Promise.all([
        metricsApi.get(),
        debtorApi.list(),
        debtorApi.listPromises(),
        authApi.usage().catch(() => null),
      ]);
      setM(metrics);
      // Excluye Pagados / saldo 0 del Top-5 (los pagados ya no son prioridad de gestión).
      const activos = debtors.filter(
        (d) => d.status !== "Pagado" && Number(d.saldo_pendiente || 0) > 0.01
      );
      setTop(activos.slice(0, 5));
      setActivosCount(activos.length);
      setPromises(prom);
      setUsage(usg);
    } catch {
      toast.error("Error cargando métricas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    try {
      const r = await debtorApi.seedDemo();
      toast.success(r.message);
      load();
    } catch {
      toast.error("No se pudo cargar demo");
    }
  };

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const r = await debtorApi.sweepPromises();
      if (r.processed_count === 0) {
        toast.info("No hay promesas incumplidas para seguimiento hoy");
      } else {
        toast.success(`${r.processed_count} seguimientos de promesa generados`);
      }
      load();
    } catch {
      toast.error("Error ejecutando barrido");
    } finally {
      setSweeping(false);
    }
  };

  // Memoized: evita recomputar en cada render y evita re-renders de Recharts
  const riskData = useMemo(
    () => (m ? Object.entries(m.por_riesgo).map(([name, value]) => ({ name, value })) : []),
    [m]
  );
  const statusData = useMemo(
    () =>
      m
        ? Object.entries(m.por_estado).map(([name, value]) => ({
            name: name.length > 12 ? name.slice(0, 10) + "…" : name,
            value,
          }))
        : [],
    [m]
  );
  const isEmpty = !m || m.total_deudas === 0;

  if (loading) return <div className="text-muted-foreground">Cargando…</div>;

  if (isEmpty) {
    return (
      <div className="max-w-xl mx-auto text-center py-16" data-testid="empty-dashboard">
        <div className="w-16 h-16 rounded-md bg-primary/10 grid place-items-center mx-auto mb-4">
          <TrendUp size={28} weight="duotone" className="text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-semibold">Bienvenido a Recobro</h2>
        <p className="text-muted-foreground mt-2">
          Comience cargando sus deudores desde Excel o dando de alta clientes manualmente para que los agentes IA hagan el resto.
        </p>
        <div className="mt-6 flex gap-3 justify-center flex-wrap">
          {user?.email === "admin@recobro.ec" && (
            <Button variant="outline" data-testid="seed-demo-btn" onClick={handleSeed}>
              Cargar deudores demo
            </Button>
          )}
          <Link to="/deudores">
            <Button data-testid="go-debtors-btn">
              Ir a Cartera
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline">Resumen ejecutivo</div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">
            Situación actual de tu cartera
          </h2>
        </div>
        <a
          href={metricsApi.balancePdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-balance-pdf"
        >
          <Button variant="outline">
            <DownloadSimple size={16} weight="bold" className="mr-2" />
            Descargar balance en PDF
          </Button>
        </a>
      </div>

      {usage && usage.limite_deudores > 0 && (usage.deudores_actuales / usage.limite_deudores) >= 0.8 && (
        (() => {
          const pct = Math.min(100, Math.round((usage.deudores_actuales / usage.limite_deudores) * 100));
          const atLimit = usage.deudores_actuales >= usage.limite_deudores;
          return (
            <div
              data-testid="usage-limit-alert"
              className={`rounded-md border p-4 flex items-start gap-3 ${
                atLimit
                  ? "border-destructive/40 bg-destructive/5 text-destructive"
                  : "border-warning/40 bg-warning/5 text-warning"
              }`}
            >
              <Warning size={22} weight="duotone" className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm leading-tight">
                  {atLimit
                    ? "Has alcanzado el límite de deudores de tu plan"
                    : `Estás usando el ${pct}% del límite de tu plan`}
                </div>
                <div className="text-xs mt-1 text-foreground/70 leading-relaxed">
                  Tienes <span className="font-mono font-semibold text-foreground">{usage.deudores_actuales}</span> de{" "}
                  <span className="font-mono font-semibold text-foreground">{usage.limite_deudores}</span> deudores activos en tu plan{" "}
                  <span className="font-semibold text-foreground">{usage.plan}</span>.
                  {atLimit
                    ? " Solicita ampliación al equipo de Recobro para seguir agregando."
                    : " Considera ampliar tu plan antes de quedarte sin cupo."}
                </div>
              </div>
            </div>
          );
        })()
      )}

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6">
        <Kpi label="Cartera vencida" value={fmt(m.total_monto_vencido)} sub={`${activosCount} deudores activos`} icon={CurrencyDollar} testid="kpi-cartera" />
        <Kpi label="Cartera comprometida" value={fmt(m.total_monto_comprometido || 0)} sub="Con promesa de pago" icon={HandCoins} testid="kpi-comprometida" />
        <Kpi label="Tasa de apertura" value={`${m.tasa_apertura}%`} sub="Mensajes generados / total" icon={Envelope} testid="kpi-apertura" />
        <Kpi label="Promesas de pago" value={m.promesas_pago} sub="Convenios obtenidos" icon={HandCoins} testid="kpi-promesas" />
        <Kpi label="Dinero recuperado" value={fmt(m.dinero_recuperado)} sub={m.abonos_parciales ? `Incluye ${m.abonos_parciales} abono(s) parcial(es)` : "Pagos + abonos confirmados"} icon={TrendUp} testid="kpi-recuperado" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-card border border-border rounded-md p-5">
          <div className="overline mb-4">Distribución por riesgo</div>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={riskData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {riskData.map((entry) => (
                    <Cell key={entry.name} fill={riskColors[entry.name]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 justify-center">
            {riskData.map((r) => (
              <div key={r.name} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: riskColors[r.name] }} />
                <span className="text-muted-foreground">{r.name}</span>
                <span className="font-medium">{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-md p-5">
          <div className="overline mb-4">Estado de cartera</div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(154 58% 16%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md">
        <div className="px-5 py-4 flex items-center justify-between border-b border-border">
          <div>
            <div className="overline">Prioridad máxima</div>
            <h3 className="font-heading font-semibold mt-0.5">Top 5 deudores a gestionar</h3>
          </div>
          <Link to="/deudores">
            <Button variant="ghost" size="sm" data-testid="view-all-debtors">
              Ver todos <ArrowRight size={14} weight="bold" className="ml-1" />
            </Button>
          </Link>
        </div>
        <div className="divide-y divide-border">
          {top.map((d) => (
            <Link
              key={d.id}
              to={`/deudores/${d.id}`}
              data-testid={`top-debtor-${d.id}`}
              className="flex items-center justify-between px-5 py-3 hover:bg-accent/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.cliente_nombre}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {d.dias_vencimiento}d vencidos · {d.email}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <RiskBadge risk={d.risk} />
                <div className="font-mono text-sm font-medium">{fmt(d.monto_deuda)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Semáforo de Promesas */}
      <div className="bg-card border border-border rounded-md" data-testid="semaforo-section">
        <div className="px-5 py-4 flex items-center justify-between border-b border-border">
          <div>
            <div className="overline">Cierre de Círculo</div>
            <h3 className="font-heading font-semibold mt-0.5">Semáforo de Promesas</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Barrido automático diario a las 09:00 AM (Ecuador). Dispara manual cuando necesite.
            </p>
          </div>
          <Button onClick={handleSweep} disabled={sweeping} variant="outline" data-testid="sweep-promises-btn">
            <Lightning size={16} weight="duotone" className="mr-2" />
            {sweeping ? "Ejecutando…" : "Ejecutar barrido ahora"}
          </Button>
        </div>
        {promises.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="empty-promises">
            Aún no hay promesas de pago activas. Analice respuestas de deudores para generarlas.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
            {["rojo", "naranja", "azul"].map((color) => {
              const group = promises.filter((p) => p.semaforo === color);
              const config = {
                rojo: { label: "Incumplidas", dot: "bg-destructive", textTone: "text-destructive", desc: "Vencidas sin comprobante" },
                naranja: { label: "Vencen hoy", dot: "bg-warning", textTone: "text-warning", desc: "Alerta de seguimiento" },
                azul: { label: "A futuro", dot: "bg-chart-3", textTone: "text-chart-3", desc: "Todo bajo control" },
              }[color];
              return (
                <div key={color} className="p-5 space-y-3" data-testid={`semaforo-${color}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${config.dot}`} />
                      <span className={`font-heading font-semibold text-sm ${config.textTone}`}>{config.label}</span>
                    </div>
                    <span className="font-mono text-sm font-medium">{group.length}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{config.desc}</div>
                  {group.length === 0 ? (
                    <div className="text-xs text-muted-foreground/60 italic">Sin casos</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {group.slice(0, 4).map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/deudores/${p.id}`}
                            data-testid={`promise-${p.id}`}
                            className="flex items-center justify-between text-xs py-1.5 px-2 rounded-sm hover:bg-accent/60 transition-colors"
                          >
                            <span className="truncate max-w-[140px]">{p.cliente_nombre}</span>
                            <span className="font-mono font-medium">{fmt(p.promesa_monto)}</span>
                          </Link>
                        </li>
                      ))}
                      {group.length > 4 && (
                        <li className="text-xs text-muted-foreground text-center pt-1">+{group.length - 4} más</li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
